import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, { CellPrimary, CellSecondary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageContainer from 'components/PageContainer';
import {
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
} from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import { useGetTrackTransactionsGetEvent } from 'components/Transactions/gql/useGetTrackTransactionsGetEvent';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import {
  formatEventDate,
  formatPhoneNumber,
  formatTimestampEST,
} from 'shared/formatters';
import {
  TransactionFilterProvider,
  useTransactionFilter,
} from 'shared/transactionFilterContext';

// Filter options
const OrderOptions = [
  { label: 'Last Name', value: 'name' },
  { label: 'Date', value: 'date' },
];

const SourceOptions = [
  { label: 'App', value: 'app' },
  { label: 'POS', value: 'pos' },
];

const UserOptions = [
  { label: 'Known', value: 'known' },
  { label: 'Anonymous', value: 'anonymous' },
];

const PaymentOptions = [
  { label: 'Card', value: 'card' },
  { label: 'Cash', value: 'cash' },
];

const DeliveryOptions = [
  { label: 'App', value: 'app' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Ticket', value: 'ticket' },
];

// Profit display styled like LifetimeValue on CustomerDetail
const ProfitContainer = styled.div`
  text-align: center;
  padding: 13px 21px;
  background: rgba(250, 70, 22, 0.05);
  border: 1px solid rgba(250, 70, 22, 0.2);
  border-radius: 8px;
  margin-left: auto;
`;

const ProfitAmount = styled.div`
  font-size: 33px;
  font-weight: 700;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  letter-spacing: -1px;
  line-height: 1;
  margin-bottom: 4px;
`;

const ProfitLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

// Helper to check if string is a phone number
const is_cellphone = (value) => {
  if (!value) return false;
  const cleaned = value.replace(/\D/g, '');
  return cleaned.length >= 10 && /^\d+$/.test(cleaned);
};

const TrackEventTransactionsContent = () => {
  const [transactions, setTransactions] = useState([]);
  const [count, setCount] = useState(0);
  const [event, setEvent] = useState(null);

  // Get filter state from context
  const {
    sortBy,
    setSortBy,
    source,
    setSource,
    userType,
    setUserType,
    paymentMethod,
    setPaymentMethod,
    deliveryMethod,
    setDeliveryMethod,
    startDateFilter,
    setStartDateFilter,
    endDateFilter,
    setEndDateFilter,
  } = useTransactionFilter();

  // Pending filter state for TableFilter (before apply)
  const [pendingSortBy, setPendingSortBy] = useState(sortBy?.value || '');
  const [pendingSource, setPendingSource] = useState(source?.value || '');
  const [pendingUserType, setPendingUserType] = useState(userType?.value || '');
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(
    paymentMethod?.value || ''
  );
  const [pendingDeliveryMethod, setPendingDeliveryMethod] = useState(
    deliveryMethod?.value || ''
  );
  const [pendingStartDate, setPendingStartDate] = useState(
    startDateFilter || ''
  );
  const [pendingEndDate, setPendingEndDate] = useState(endDateFilter || '');

  const { pathname } = useLocation();
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Get transactions and event data
  const { data, loading } = useGetTrackTransactionsGetEvent();

  useEffect(() => {
    if (data?.getTrackTransactionsV2) {
      setTransactions(data.getTrackTransactionsV2.results || []);
      setCount(data.getTrackTransactionsV2.count || 0);
    }
    if (data?.getEvent) {
      setEvent(data.getEvent);
    }
  }, [data]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle filter change - updates pending state for dynamic filter options
  const handleFilterChange = (newFilters) => {
    if (newFilters.sortBy !== undefined) {
      setPendingSortBy(newFilters.sortBy || '');
    }
    if (newFilters.source !== undefined) {
      setPendingSource(newFilters.source || '');
      // Reset dependent filters when source changes
      if (newFilters.source !== 'pos') {
        setPendingUserType('');
        setPendingPaymentMethod('');
        setPendingDeliveryMethod('');
      }
    }
    if (newFilters.userType !== undefined) {
      setPendingUserType(newFilters.userType || '');
    }
    if (newFilters.paymentMethod !== undefined) {
      setPendingPaymentMethod(newFilters.paymentMethod || '');
    }
    if (newFilters.deliveryMethod !== undefined) {
      setPendingDeliveryMethod(newFilters.deliveryMethod || '');
    }
    if (newFilters.startDate !== undefined) {
      setPendingStartDate(newFilters.startDate || '');
    }
    if (newFilters.endDate !== undefined) {
      setPendingEndDate(newFilters.endDate || '');
    }
  };

  // Handle filter apply - updates applied state to trigger main query
  const handleApplyFilters = (newFilters) => {
    // Update context state which triggers query refetch
    setSortBy(
      newFilters.sortBy
        ? OrderOptions.find((o) => o.value === newFilters.sortBy)
        : null
    );
    setSource(
      newFilters.source
        ? SourceOptions.find((o) => o.value === newFilters.source)
        : null
    );
    setUserType(
      newFilters.userType
        ? UserOptions.find((o) => o.value === newFilters.userType)
        : null
    );
    setPaymentMethod(
      newFilters.paymentMethod
        ? PaymentOptions.find((o) => o.value === newFilters.paymentMethod)
        : null
    );
    setDeliveryMethod(
      newFilters.deliveryMethod
        ? DeliveryOptions.find((o) => o.value === newFilters.deliveryMethod)
        : null
    );
    setStartDateFilter(newFilters.startDate || '');
    setEndDateFilter(newFilters.endDate || '');

    // Sync pending state with applied
    setPendingSortBy(newFilters.sortBy || '');
    setPendingSource(newFilters.source || '');
    setPendingUserType(newFilters.userType || '');
    setPendingPaymentMethod(newFilters.paymentMethod || '');
    setPendingDeliveryMethod(newFilters.deliveryMethod || '');
    setPendingStartDate(newFilters.startDate || '');
    setPendingEndDate(newFilters.endDate || '');

    updateQuery({ page: 1 });
  };

  // Handle filter clear
  const handleClearFilters = () => {
    setSortBy(null);
    setSource(null);
    setUserType(null);
    setPaymentMethod(null);
    setDeliveryMethod(null);
    setStartDateFilter('');
    setEndDateFilter('');

    setPendingSortBy('');
    setPendingSource('');
    setPendingUserType('');
    setPendingPaymentMethod('');
    setPendingDeliveryMethod('');
    setPendingStartDate('');
    setPendingEndDate('');

    updateQuery({ page: 1 });
  };

  // Build filter configuration for TableFilter
  const filterConfig = [
    {
      key: 'sortBy',
      label: 'Sort By',
      type: 'select',
      placeholder: 'Default',
      options: OrderOptions,
    },
    {
      key: 'source',
      label: 'Source',
      type: 'select',
      placeholder: 'All Sources',
      options: SourceOptions,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date',
      placeholder: 'Select Start Date',
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'date',
      placeholder: 'Select End Date',
    },
  ];

  // Add POS-specific filters only when source is 'pos'
  if (pendingSource === 'pos') {
    filterConfig.push({
      key: 'userType',
      label: 'User Type',
      type: 'select',
      placeholder: 'All Users',
      options: UserOptions,
    });
    filterConfig.push({
      key: 'paymentMethod',
      label: 'Payment Method',
      type: 'select',
      placeholder: 'All Payments',
      options: PaymentOptions,
    });
    filterConfig.push({
      key: 'deliveryMethod',
      label: 'Delivery Method',
      type: 'select',
      placeholder: 'All Delivery',
      options: DeliveryOptions,
    });
  }

  // Current filter values for TableFilter
  const filterValues = {
    sortBy: pendingSortBy,
    source: pendingSource,
    userType: pendingUserType,
    paymentMethod: pendingPaymentMethod,
    deliveryMethod: pendingDeliveryMethod,
    startDate: pendingStartDate,
    endDate: pendingEndDate,
  };

  // DataTable columns
  const columns = [
    {
      field: 'purchase_date',
      header: 'Date/Time',
      render: (transaction) => (
        <CellSecondary>
          {formatTimestampEST(Number(transaction.purchase_date))}
        </CellSecondary>
      ),
    },
    {
      field: 'user',
      header: 'Purchaser Name',
      mobileTitle: true,
      render: (transaction) => {
        const displayName = transaction.user
          ? is_cellphone(transaction.user.last_name)
            ? formatPhoneNumber(transaction.user.last_name)
            : `${transaction.user.last_name}, ${transaction.user.first_name}`
          : `Transaction #${transaction.id}`;

        return <CellPrimary>{displayName}</CellPrimary>;
      },
    },
    {
      field: 'subtotal',
      header: 'Order Total',
      render: (transaction) => transaction.subtotal ?? '$0.00',
    },
    {
      field: 'discount',
      header: 'Discount',
      render: (transaction) =>
        transaction.discount ? `-${transaction.discount}` : '-$0.00',
    },
    {
      field: 'refund',
      header: 'Refund',
      render: (transaction) =>
        transaction.refund ? `-${transaction.refund}` : '-$0.00',
    },
    {
      field: 'credit',
      header: 'Credit',
      render: (transaction) =>
        transaction.credit ? `-${transaction.credit}` : '-$0.00',
    },
    {
      field: 'fee',
      header: 'Fee',
      render: (transaction) =>
        transaction.fee ? `${transaction.fee}` : '$0.00',
    },
    {
      field: 'total',
      header: 'Transaction Total',
      mobileSubtitle: true,
      render: (transaction) => (
        <CellPrimary>{transaction.total ?? '$0.00'}</CellPrimary>
      ),
    },
  ];

  // DataTable actions
  const actions = [
    {
      type: 'view',
      title: 'View Details',
      mobileLabel: 'View',
      to: (transaction) => ({
        pathname: `${routePrefix}/transactions/detail/${transaction.id}`,
        state: { event },
      }),
    },
  ];

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="transactions" />
            <div>
              <HeaderTitle>{event?.name || 'Event'} - Transactions</HeaderTitle>
              <HeaderSubtitle>
                {event?.track?.name || 'Track'} - {formatEventDate(event)}
              </HeaderSubtitle>
            </div>
            {event?.sales && (
              <ProfitContainer>
                <ProfitAmount>{event.sales}</ProfitAmount>
                <ProfitLabel>Event Profit</ProfitLabel>
              </ProfitContainer>
            )}
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search and Filters */}
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search by last name..."
                debounceMs={500}
              />
              <TableFilter
                filters={filterConfig}
                values={filterValues}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </PageToolbar>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={transactions}
              loading={loading}
              keyField="id"
              emptyMessage="No transactions found"
              emptySubtext="Try adjusting your search or filters"
              actions={actions}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

// Wrap with filter context provider
const TrackEventTransactions = (props) => (
  <TransactionFilterProvider>
    <TrackEventTransactionsContent {...props} />
  </TransactionFilterProvider>
);

export default TrackEventTransactions;
